/**
 * docs/05-ADMIN-DASHBOARD.md §2 — the route map, as data.
 *
 * One table drives three things that must not disagree: the router, the
 * sidebar, and the ⌘K palette. When they are written separately the palette is
 * the one that rots — it keeps offering a screen the sidebar stopped showing,
 * and the person who finds out is an editor who lands on a 403.
 *
 * `permission` is the §3 name the *server* enforces. Hiding an entry the user
 * cannot use is a courtesy (§1: "groups are hidden entirely when the user has
 * no permission for anything inside them"); the Policy on the endpoint is the
 * control. Anything reachable by any signed-in account has `permission: null`.
 */
export type NavGroupKey =
  'dashboard' | 'content' | 'academic' | 'vocational' | 'library' | 'site' | 'system';

export interface NavItem {
  /** i18n key under `nav.items`, and the palette's search key. */
  key: string;
  path: string;
  /** §3's `{resource}.{action}`, or null for "any signed-in user". */
  permission: string | null;
  /** Super-Admin-only screens (§5.9), which no single permission covers. */
  superAdminOnly?: boolean;
  /** lucide icon name, resolved in `NavIcon`. */
  icon: string;
  /**
   * Kept out of the sidebar but still routed and still searchable — taxonomy
   * screens reached from the list they belong to, and the profile page that
   * lives in the avatar menu.
   */
  hidden?: boolean;
}

export interface NavGroup {
  key: NavGroupKey;
  items: NavItem[];
}

export const NAVIGATION: NavGroup[] = [
  {
    key: 'dashboard',
    items: [{ key: 'dashboard', path: '/', permission: null, icon: 'layout-dashboard' }],
  },
  {
    key: 'content',
    items: [
      { key: 'news', path: '/news', permission: 'news.view', icon: 'newspaper' },
      {
        key: 'newsCategories',
        path: '/news-categories',
        permission: 'news.manage-taxonomy',
        icon: 'folder-tree',
        hidden: true,
      },
      {
        key: 'tags',
        path: '/tags',
        permission: 'news.manage-taxonomy',
        icon: 'tags',
        hidden: true,
      },
      {
        key: 'announcements',
        path: '/announcements',
        permission: 'announcements.view',
        icon: 'megaphone',
      },
      { key: 'events', path: '/events', permission: 'events.view', icon: 'calendar-days' },
      { key: 'clubs', path: '/clubs', permission: 'clubs.view', icon: 'users-round' },
      { key: 'pages', path: '/pages', permission: 'pages.view', icon: 'file-text' },
      /*
       * §5.2 calls this "the most important screen for keeping the designed
       * pages fresh without a developer", so it is in the sidebar rather than
       * buried under Pages. §2 routes it as `/page-sections/:pageKey`; the
       * index lists the page keys, which the table does not name because it
       * lists screens rather than every path.
       */
      {
        key: 'pageSections',
        path: '/page-sections',
        permission: 'pages.update',
        icon: 'layout-panel-top',
      },
      { key: 'modals', path: '/modals', permission: 'pages.update', icon: 'square-stack' },
    ],
  },
  {
    key: 'academic',
    items: [
      { key: 'faculties', path: '/faculties', permission: 'faculties.view', icon: 'landmark' },
      {
        key: 'departments',
        path: '/departments',
        permission: 'faculties.update',
        icon: 'git-branch',
      },
      { key: 'programs', path: '/programs', permission: 'programs.view', icon: 'graduation-cap' },
      { key: 'staff', path: '/staff', permission: 'staff.view', icon: 'contact' },
    ],
  },
  {
    key: 'vocational',
    items: [
      {
        key: 'vocationalPrograms',
        path: '/vocational/programs',
        permission: 'vocational.view',
        icon: 'wrench',
      },
      {
        key: 'vocationalContacts',
        path: '/vocational/contacts',
        permission: 'vocational.view',
        icon: 'phone',
      },
    ],
  },
  {
    key: 'library',
    items: [
      { key: 'documents', path: '/documents', permission: 'documents.view', icon: 'file-down' },
      {
        key: 'documentCategories',
        path: '/document-categories',
        permission: 'documents.manage-taxonomy',
        icon: 'folder-tree',
        hidden: true,
      },
      { key: 'media', path: '/media', permission: 'media.view', icon: 'image' },
    ],
  },
  {
    key: 'site',
    items: [
      { key: 'projects', path: '/projects', permission: 'projects.view', icon: 'flask-conical' },
      { key: 'vacancies', path: '/vacancies', permission: 'vacancies.view', icon: 'briefcase' },
      { key: 'menus', path: '/menus', permission: 'menus.view', icon: 'list-tree' },
      {
        key: 'usefulLinks',
        path: '/useful-links',
        // §2 puts this under `settings.update`, not `menus.*` — the tiles are
        // site configuration rather than navigation.
        permission: 'settings.update',
        icon: 'link',
      },
      { key: 'settings', path: '/settings', permission: 'settings.view', icon: 'settings' },
      { key: 'submissions', path: '/submissions', permission: 'submissions.view', icon: 'inbox' },
      { key: 'redirects', path: '/redirects', permission: 'redirects.view', icon: 'signpost' },
    ],
  },
  {
    key: 'system',
    items: [
      { key: 'users', path: '/users', permission: 'users.view', icon: 'user-cog' },
      { key: 'roles', path: '/roles', permission: 'roles.view', icon: 'shield-check' },
      { key: 'activity', path: '/activity', permission: 'activity.view', icon: 'history' },
      {
        key: 'migration',
        path: '/tools/migration',
        /*
         * §5.9 — "Super Admin only", and now checked twice.
         *
         * `migration.view` exists as of the console itself: it is granted to
         * nobody in `Permissions::ROLE_GRANTS`, and the Super Admin holds every
         * permission automatically, so naming it changes nothing about who gets
         * in. What it changes is that the gate is the same shape as the other
         * twenty and the API answers to the same rule as the sidebar.
         */
        permission: 'migration.view',
        superAdminOnly: true,
        icon: 'database-backup',
      },
      {
        key: 'profile',
        path: '/profile',
        permission: null,
        icon: 'user',
        // Reached from the avatar menu, per §1's header.
        hidden: true,
      },
    ],
  },
];

/** Every item, in sidebar order — the router and the palette both walk this. */
export const NAV_ITEMS: NavItem[] = NAVIGATION.flatMap((group) => group.items);

export interface Viewer {
  can: (permission: string) => boolean;
  isSuperAdmin: boolean;
}

export function isVisibleTo(item: NavItem, viewer: Viewer): boolean {
  if (item.superAdminOnly && !viewer.isSuperAdmin) {
    return false;
  }

  return item.permission === null || viewer.can(item.permission);
}

/**
 * The groups this viewer has anything in, with their hidden items dropped.
 *
 * §1 — "groups are hidden entirely when the user has no permission for anything
 * inside them". A Document Manager should not be looking at an empty CONTENT
 * heading wondering what is missing.
 */
export function visibleNavigation(viewer: Viewer): NavGroup[] {
  return NAVIGATION.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.hidden && isVisibleTo(item, viewer)),
  })).filter((group) => group.items.length > 0);
}
